import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import { AuthorizedPerson } from '../api/families';
import { useAuth } from '../context/AuthProvider';

interface AuthorizedPersonCardProps {
  person: AuthorizedPerson;
  onEdit: (person: AuthorizedPerson) => void;
  onDelete: (person: AuthorizedPerson) => void;
}

const AuthorizedPersonCard: React.FC<AuthorizedPersonCardProps> = ({
  person,
  onEdit,
  onDelete,
}) => {
  const { user } = useAuth();

  // Check if this authorized person is the primary contact
  const isPrimaryContact = person.user_id === user?.id;
  const handlePhoneClick = () => {
    window.location.href = `tel:${person.phone}`;
  };

  const handleEmailClick = () => {
    if (person.email) {
      window.location.href = `mailto:${person.email}`;
    }
  };

  return (
    <Card
      variant={person.has_pickup_rights ? 'student-ready' : 'info-card'}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
        {/* Header Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <PersonIcon color="action" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="cardTitle">
                  {person.first_name} {person.last_name}
                </Typography>
                {isPrimaryContact && (
                  <Chip
                    icon={<StarIcon />}
                    label="Primary Contact"
                    size="small"
                    color="primary"
                    variant="filled"
                    sx={{ height: '20px', '& .MuiChip-label': { px: 1, fontSize: '0.75rem' } }}
                  />
                )}
              </Box>
              <Typography variant="cardSubtitle">
                {person.relationship}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => onEdit(person)}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            {!isPrimaryContact && (
              <IconButton
                size="small"
                onClick={() => onDelete(person)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main' },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Permission Chips */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {person.has_pickup_rights && (
            <Chip
              icon={<CheckCircleIcon />}
              label="Can Pick Up"
              size="small"
              color="success"
              variant="filled"
            />
          )}
          {person.id_check_required && (
            <Chip
              icon={<BadgeIcon />}
              label="ID Required"
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
        </Box>

        {/* Contact Information */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box
            onClick={handlePhoneClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              borderRadius: 1,
              bgcolor: 'background.paper',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <PhoneIcon sx={{ color: 'primary.main', fontSize: '1.25rem' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {person.phone}
            </Typography>
          </Box>

          {person.email && (
            <Box
              onClick={handleEmailClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: 1,
                bgcolor: 'background.paper',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <EmailIcon sx={{ color: 'primary.main', fontSize: '1.25rem' }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {person.email}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AuthorizedPersonCard;
